import React from 'react';
import { Box, Container, Typography, Stack, Button, Divider, Chip } from '@mui/material';
import Grid from '@mui/material/Grid';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RateReviewIcon from '@mui/icons-material/RateReview';
import InboxIcon from '@mui/icons-material/Inbox';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ShieldIcon from '@mui/icons-material/Shield';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsightsIcon from '@mui/icons-material/Insights';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GavelIcon from '@mui/icons-material/Gavel';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Header from './Header';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepCard({ num, time, title, body, accent }: { num: number; time: string; title: string; body: string; accent: string }) {
  return (
    <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
      <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: `${accent}18`, border: `1px solid ${accent}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 13, color: accent }}>{num}</Typography>
        </Box>
        {num < 4 && <Box sx={{ width: 1, height: 32, bgcolor: `${accent}20` }} />}
      </Box>
      <Box sx={{ pb: num < 4 ? 3 : 0 }}>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: accent, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}>{time}</Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8', mb: 0.75 }}>{title}</Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>{body}</Typography>
      </Box>
    </Box>
  );
}

function PhoneMockup() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: 260, borderRadius: '32px', bgcolor: '#12121e', border: '6px solid #22223a', boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)', overflow: 'hidden', position: 'relative' }}>
        {/* Camera notch */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5 }}>
          <Box sx={{ width: 80, height: 6, borderRadius: 3, bgcolor: '#22223a' }} />
        </Box>
        {/* Status bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2.5, pb: 1 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, color: '#9499b0', fontWeight: 600 }}>9:02 AM</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, color: '#9499b0' }}>●●● ▲ 🔋</Typography>
        </Box>

        {/* Lock screen bg */}
        <Box sx={{ bgcolor: '#0d0d1a', px: 2, pb: 2.5 }}>
          {/* App label */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
            <Box sx={{ width: 20, height: 20, borderRadius: '6px', bgcolor: '#5ce1e6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShieldIcon sx={{ fontSize: 12, color: '#0a0a12' }} />
            </Box>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 10, color: '#9499b0' }}>SMCLA · now</Typography>
          </Box>

          {/* Alert 1 — High severity */}
          <Box sx={{ p: 1.75, bgcolor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ff6b6b', flexShrink: 0 }} />
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 10, color: '#ff6b6b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>High Severity</Typography>
            </Box>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color: '#f0f0f8', mb: 0.5 }}>Threatening Language</Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: '#9499b0' }}>Room 3 · 8:58 AM · Tap to review →</Typography>
          </Box>

          {/* Alert 2 — Medium severity */}
          <Box sx={{ p: 1.75, bgcolor: 'rgba(255,159,67,0.08)', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ff9f43', flexShrink: 0 }} />
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 10, color: '#ff9f43', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Medium Severity</Typography>
            </Box>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color: '#f0f0f8', mb: 0.5 }}>Child Distress</Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: '#9499b0' }}>Room 1 · 8:52 AM · Tap to review →</Typography>
          </Box>

          {/* Alert 3 — Low severity */}
          <Box sx={{ p: 1.75, bgcolor: 'rgba(92,225,230,0.06)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5ce1e6', flexShrink: 0 }} />
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 10, color: '#5ce1e6', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Low Severity</Typography>
            </Box>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color: '#f0f0f8', mb: 0.5 }}>Noise Threshold</Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: '#9499b0' }}>Room 2 · 8:47 AM · Tap to review →</Typography>
          </Box>
        </Box>

        {/* Home bar */}
        <Box sx={{ display: 'flex', justifyContent: 'center', bgcolor: '#0d0d1a', pb: 2, pt: 1 }}>
          <Box sx={{ width: 80, height: 4, borderRadius: 2, bgcolor: '#22223a' }} />
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const alertGroups = [
  {
    label: 'Emotional & Audio',
    color: '#ff9f43',
    icon: <VolumeUpIcon sx={{ fontSize: 16 }} />,
    types: ['Threatening Language', 'Demanding Language', 'Child Distress', 'Prolonged Distress', 'Verbal Confrontation'],
  },
  {
    label: 'Physical',
    color: '#5ce1e6',
    icon: <ShieldIcon sx={{ fontSize: 16 }} />,
    types: ['Physical Contact', 'Behavioral Concern', 'Child Neglect', 'Supervision Gap', 'Worker Overload'],
  },
  {
    label: 'Operational',
    color: '#8c52ff',
    icon: <BusinessIcon sx={{ fontSize: 16 }} />,
    types: ['Unauthorized Access', 'Safety Hazard', 'Noise Threshold'],
  },
  {
    label: 'Technical',
    color: '#9499b0',
    icon: <BuildIcon sx={{ fontSize: 16 }} />,
    types: ['Sensor Alert', 'Other'],
  },
];

const escalationPaths = [
  { icon: <FamilyRestroomIcon sx={{ color: '#ff6b6b', fontSize: 22 }} />, title: 'Child Protective Services', body: 'Abuse or neglect requiring mandatory reporting to state CPS agencies.', color: '#ff6b6b' },
  { icon: <LocalPoliceIcon sx={{ color: '#ff9f43', fontSize: 22 }} />, title: 'Local Law Enforcement', body: 'Immediate safety threats, criminal activity, or emergency response needed.', color: '#ff9f43' },
  { icon: <AccountBalanceIcon sx={{ color: '#8c52ff', fontSize: 22 }} />, title: 'Childcare Licensing Board', body: 'Regulatory violations requiring state licensing authority notification.', color: '#8c52ff' },
  { icon: <BusinessIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />, title: 'District Manager', body: 'Multi-location oversight and internal escalation for chain operators.', color: '#5ce1e6' },
  { icon: <GavelIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />, title: 'Legal Counsel', body: 'Documentation for litigation, internal review, or HR proceedings.', color: '#5ce1e6' },
  { icon: <HealthAndSafetyIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />, title: 'Insurance Provider', body: 'Risk management notification and claims documentation support.', color: '#5ce1e6' },
  { icon: <ManageSearchIcon sx={{ color: '#ff9f43', fontSize: 22 }} />, title: 'Forensic Investigation', body: 'Full multi-camera reconstruction with 24-hour footage buffer, exported as a chain-of-custody evidence package ready for law enforcement or court submission.', color: '#ff9f43' },
];

const personas = [
  {
    role: 'Owner / Admin',
    roleIcon: <AdminPanelSettingsIcon sx={{ fontSize: 18 }} />,
    accent: '#5ce1e6',
    name: 'Sarah',
    companyRole: 'Daycare Owner & Director',
    accessLevel: 'Full Access',
    responsibilities: [
      'Oversees all staff, rooms, and daily operations',
      'Final decision-maker on disciplinary actions and escalations',
      'Responsible for licensing, compliance, and parent relations',
    ],
    description: 'Full platform access: Dashboard, Alerts, Incidents, Team management, and System setup.',
    photo: 'https://images.unsplash.com/photo-1573497491765-dccce02b29df?auto=format&fit=crop&w=600&q=80',
    photoAlt: 'Daycare owner at her desk reviewing the SMCLA dashboard',
    steps: [
      { time: '7:58 AM', title: 'Push notification arrives', body: 'High severity alert: Threatening Language detected in Room 3. The alert is on her phone before she reaches the facility — 60 seconds after detection.' },
      { time: '8:01 AM', title: 'Opens the full analysis', body: 'Dashboard shows 3 pending alerts. She opens the high-severity one: audio waveform, transcript with flagged phrases highlighted, sentiment timeline dropping to -0.9.' },
      { time: '8:06 AM', title: 'Takes action', body: 'Marks as True Alert. Selects "Verbal Warning." Notifies Reviewer Marcus and Reviewer Aisha. Alert moves to the Reviewed table.' },
      { time: '8:08 AM', title: 'Incident logged permanently', body: 'Action recorded: timestamp, classification, action taken, team notified. Available in Incidents log for licensing audits. Evidence preserved.' },
      { time: '8:10 AM', title: 'Investigation opened — full facility reconstruction', body: 'Sarah opens an investigation with one click. SMCLA locks the 24-hour footage buffer and surfaces all cameras: Room 3, adjacent hallway, entry point. The complete picture — not just the flagged clip — is ready to export for law enforcement or legal counsel.' },
    ],
    stat: 'Under 10 minutes from alert to court-ready investigation package.',
  },
  {
    role: 'Reviewer',
    roleIcon: <RateReviewIcon sx={{ fontSize: 18 }} />,
    accent: '#8c52ff',
    name: 'Marcus',
    companyRole: 'Safety Reviewer',
    accessLevel: 'Alert Review',
    responsibilities: [
      'Classifies incoming alerts as true or false',
      'Reviews AI analysis, video, audio, and transcripts',
      'Coordinates with Admin on actions and escalations',
    ],
    description: 'Reviews and classifies alerts. Can view the dashboard and incidents but does not manage team or system settings.',
    photo: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=600&q=80',
    photoAlt: 'Alert reviewer analyzing incident on laptop',
    steps: [
      { time: '2:03 PM', title: 'Alert lands in his queue', body: 'Marcus receives a shared alert notification. He never had to watch all 6 cameras — the AI flagged it and Sarah handed it to him directly.' },
      { time: '2:05 PM', title: 'Full AI analysis open', body: 'Video shows a physical contact event in Room 2. Visual Event Timeline: 3 timestamped events. Detected Objects: "Adult — confidence 98%, Child — confidence 97%."' },
      { time: '2:08 PM', title: "It's a false alarm", body: 'The AI flagged normal horseplay. Marcus clicks Dismiss → "Normal play / misidentification" → reference key FA-2025-0312-A generated. Notes added for the audit trail.' },
      { time: '2:10 PM', title: 'Pattern spotted on dashboard', body: 'Yellow banner: "Clustering: ≥20% of today\'s alerts between 2–3 PM." Marcus flags this post-lunch pattern for the weekly staff review.' },
    ],
    stat: 'False alarms are documented, not deleted — every dismissal has a reference key.',
  },
  {
    role: 'Facility Manager',
    roleIcon: <InboxIcon sx={{ fontSize: 18 }} />,
    accent: '#ff9f43',
    name: 'Aisha',
    companyRole: 'Floor / Facility Manager',
    accessLevel: 'Inbox Only',
    responsibilities: [
      'Manages day-to-day room operations and staff on the floor',
      'Receives alerts the Admin explicitly shares with her',
      'Provides context and replies to clarify specific incidents',
    ],
    description: 'Inbox-only access. Sees only alerts explicitly shared with her. Can reply to provide context. No alert fatigue.',
    photo: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=600&q=80',
    photoAlt: 'Facility manager checking her phone for alert notifications',
    steps: [
      { time: '10:15 AM', title: 'Alert shared with her inbox', body: 'Aisha opens her Inbox — no dashboard, no noise. One card: Severity Medium, Child Neglect, Room 4. Exactly the alert that matters to her, nothing else.' },
      { time: '10:17 AM', title: 'Reviews the AI summary', body: 'Expandable card: child unattended for 6 minutes without a visible caregiver. Duration metric: 6m 14s. Full context without raw footage access.' },
      { time: '10:19 AM', title: 'Sends a reply', body: 'Aisha replies: "I was in Room 4 at 10:09 — can you confirm which camera angle triggered this?" Her reply is visible to Admin in the Insights dialog.' },
      { time: '10:22 AM', title: 'Context resolves the alert', body: 'Admin reviews the reply, confirms the camera angle issue, dismisses with note. Aisha\'s reply becomes part of the permanent audit record.' },
    ],
    stat: 'Viewers see only what\'s shared with them — no access creep, no alert fatigue.',
  },
];

const scenarios = [
  {
    num: 1,
    label: 'True Alert',
    labelColor: '#ff6b6b',
    icon: <WarningAmberIcon sx={{ color: '#ff6b6b', fontSize: 22 }} />,
    title: '"You\'ll regret this."',
    subtitle: 'Threatening Language · High Severity',
    beats: [
      '8:58 AM: Audio AI detects raised voice pattern in Room 3. Flagged phrase: "You\'ll regret this."',
      'Sentiment timeline drops to -0.9. Full transcript highlighted in the analysis.',
      'Reviewer marks True Alert → Verbal Warning → team notified.',
      'Incident logged with all evidence. If pattern repeats: escalated to CPS.',
    ],
    outcome: 'Worker counseled. Incident documented. Parent trust maintained.',
    outcomeColor: '#ff6b6b',
  },
  {
    num: 2,
    label: 'False Alarm',
    labelColor: '#5ce1e6',
    icon: <CheckCircleIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: '12 children singing at once.',
    subtitle: 'Noise Threshold · Low Severity',
    beats: [
      '1:02 PM: 12 children singing during activity triggers Noise Threshold alert.',
      'Reviewer opens video — clearly normal group activity, no concern.',
      'Dismissed: "Normal play / group activity" → reference key FA-2025-0134-B generated.',
      'Zero investigation time wasted. Full dismissal audit trail preserved.',
    ],
    outcome: 'False alarm dismissed in under 3 minutes. Reference key filed.',
    outcomeColor: '#5ce1e6',
  },
  {
    num: 3,
    label: 'Pattern Detection',
    labelColor: '#8c52ff',
    icon: <InsightsIcon sx={{ color: '#8c52ff', fontSize: 22 }} />,
    title: 'The staffing gap nobody saw.',
    subtitle: 'Worker Overload · Recurring Pattern',
    beats: [
      'Dashboard banner: "Clustering: 68% of Worker Overload alerts between 9–11 AM, Mon–Thu."',
      'Admin reviews Incidents log: 14 Worker Overload alerts across 3 weeks, same window.',
      'Root cause identified: morning drop-off rush with one fewer staff than needed.',
      'Additional staff scheduled for Monday–Thursday mornings.',
    ],
    outcome: 'Staffing gap corrected before an incident occurred.',
    outcomeColor: '#8c52ff',
  },
  {
    num: 4,
    label: 'Investigation',
    labelColor: '#ff9f43',
    icon: <ManageSearchIcon sx={{ color: '#ff9f43', fontSize: 22 }} />,
    title: 'One confirmed incident. The full picture in minutes.',
    subtitle: 'True Incident · Multi-Camera Reconstruction',
    beats: [
      'Reviewer marks alert as True Incident. SMCLA immediately creates a timestamped investigation record.',
      'System locks the 24-hour rolling footage buffer — every camera in the facility, nothing lost.',
      'Owner opens investigation: full multi-camera reconstruction surfaces automatically. Adjacent rooms, hallways, entry points — all visible.',
      'Investigation package exported: clips, audit trail, chain-of-custody metadata, reviewer notes.',
      'Package submitted to law enforcement, CPS, or legal counsel — forensic-grade, court-ready.',
    ],
    outcome: 'Complete evidence package ready for authorities in minutes, not days.',
    outcomeColor: '#ff9f43',
  },
];

export default function UseCasesPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="SMCLA in Action — Every Role, Every Alert Type" description="See how owners, reviewers, and facility managers use SMCLA to detect, investigate, and document childcare incidents in real workflows." path="/use-cases" />
      <Header onBookDemo={() => navigate('/contact')} />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 14 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-5%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.07) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Real Users. Real Scenarios.
          </Typography>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2, lineHeight: 1.05 }}>
            See SMCLA working.<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>Every role. Every alert type.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 19 }, color: '#9499b0', maxWidth: 640, mx: 'auto', lineHeight: 1.65, mb: 5 }}>
            From the facility owner who gets a push alert at 9 AM, to the reviewer who classifies it by 9:05, to the facility manager who replies from their inbox — here is what SMCLA looks like from every seat.
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
            {[{ label: 'Owner', color: '#5ce1e6' }, { label: 'Reviewer', color: '#8c52ff' }, { label: 'Facility Manager', color: '#ff9f43' }].map(({ label, color }) => (
              <Box key={label} sx={{ px: 2, py: 0.75, bgcolor: `${color}12`, border: `1px solid ${color}35`, borderRadius: 10 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color, letterSpacing: '0.04em' }}>{label}</Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Persona Sections */}
      {personas.map(({ role, roleIcon, accent, name, companyRole, accessLevel, responsibilities, description, photo, photoAlt, steps, stat }, personaIdx) => (
        <Box
          key={role}
          sx={{ bgcolor: personaIdx % 2 === 1 ? '#0d0d18' : '#0a0a12', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}
        >
          <Container maxWidth="md">
            <Grid container spacing={6} alignItems="flex-start" direction={{ xs: 'column', md: personaIdx % 2 === 1 ? 'row-reverse' : 'row' }}>
              {/* Photo column */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={photo}
                    alt={photoAlt}
                    sx={{ width: '100%', maxWidth: 420, borderRadius: 3, display: 'block', aspectRatio: '4/3', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.06)' }}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <Box sx={{ mt: 2, p: 2.5, bgcolor: '#13131f', border: `1px solid ${accent}20`, borderRadius: 2 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic', fontSize: 13, color: accent }}>
                      "{stat}"
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Content column */}
              <Grid size={{ xs: 12, md: 7 }}>
                {/* Role profile card */}
                <Box sx={{ mb: 4, p: 2.5, bgcolor: '#13131f', border: `1px solid ${accent}25`, borderRadius: 3 }}>
                  {/* Name + title row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: `${accent}15`, border: `1px solid ${accent}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent, flexShrink: 0 }}>
                      {roleIcon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 17, color: '#f0f0f8', lineHeight: 1.2 }}>{name}</Typography>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.3 }}>{companyRole}</Typography>
                    </Box>
                  </Box>

                  {/* Access level chip */}
                  <Chip
                    label={accessLevel}
                    size="small"
                    sx={{ bgcolor: `${accent}15`, color: accent, border: `1px solid ${accent}35`, fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.05em', mb: 2, height: 24 }}
                  />

                  {/* Responsibilities */}
                  <Stack spacing={0.75}>
                    {responsibilities.map((r, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: accent, mt: 0.8, flexShrink: 0 }} />
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.55 }}>{r}</Typography>
                      </Box>
                    ))}
                  </Stack>

                  {/* Access summary */}
                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid rgba(255,255,255,0.06)` }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.7)', lineHeight: 1.55 }}>
                      {description}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 4 }}>
                  A day in the life of {name}.
                </Typography>

                <Stack spacing={0}>
                  {steps.map((step, i) => (
                    <StepCard key={i} num={i + 1} time={step.time} title={step.title} body={step.body} accent={accent} />
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}

      {/* Phone notification mockup */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <PhoneMockup />
              <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
                {[
                  { icon: <PhoneAndroidIcon sx={{ fontSize: 14 }} />, label: 'Push' },
                  { icon: <SmsIcon sx={{ fontSize: 14 }} />, label: 'SMS' },
                  { icon: <EmailIcon sx={{ fontSize: 14 }} />, label: 'Email' },
                ].map(({ icon, label }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.75, bgcolor: 'rgba(92,225,230,0.06)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 10, color: '#5ce1e6' }}>
                    {icon}
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#5ce1e6', fontWeight: 600 }}>{label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                <NotificationsActiveIcon sx={{ color: '#5ce1e6', fontSize: 20 }} />
                <Typography variant="overline" sx={{ color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em' }}>Real-Time Alerts</Typography>
              </Box>
              <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.3rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
                The alert reaches you in under 60 seconds.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 4 }}>
                Every user configures their own notification preferences — push, SMS, or email — and can filter by severity so high-priority alerts always break through, while low-severity noise stays in the queue.
              </Typography>
              <Stack spacing={2}>
                {[
                  { color: '#ff6b6b', label: 'High Severity', desc: 'Immediate push notification + SMS. Sounds an alarm.' },
                  { color: '#ff9f43', label: 'Medium Severity', desc: 'Push notification. Reviewed within the shift.' },
                  { color: '#5ce1e6', label: 'Low Severity', desc: 'Batched email digest. No interruption to operations.' },
                ].map(({ color, label, desc }) => (
                  <Box key={label} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8' }}>{label}</Typography>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>{desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Alert type grid */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Detection Coverage
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            15 alert types. 3 severity levels.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 560 }}>
            Every alert category maps to a real pattern of harm. No generic "anomaly detection" — each type has specific AI criteria and review workflows.
          </Typography>

          <Grid container spacing={2.5}>
            {alertGroups.map(({ label, color, icon, types }) => (
              <Grid key={label} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ bgcolor: '#13131f', border: `1px solid ${color}20`, borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                  <Box sx={{ px: 2.5, py: 2, bgcolor: `${color}10`, borderBottom: `1px solid ${color}20`, display: 'flex', alignItems: 'center', gap: 1, color }}>
                    {icon}
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color, letterSpacing: '0.04em' }}>{label}</Typography>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    <Stack spacing={1}>
                      {types.map(t => (
                        <Box key={t} sx={{ px: 1.25, py: 0.6, bgcolor: `${color}08`, border: `1px solid ${color}18`, borderRadius: 1.5, display: 'inline-block' }}>
                          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(240,240,248,0.75)', lineHeight: 1 }}>{t}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Scenario deep dives */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Scenario Walkthroughs
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Three scenarios. Three outcomes.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 560 }}>
            A true alert, a false alarm, and a pattern no one would have caught without AI. This is what the system looks like when it works.
          </Typography>

          <Stack spacing={3}>
            {scenarios.map(({ num, label, labelColor, icon, title, subtitle, beats, outcome, outcomeColor }) => (
              <Box key={num} sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '50%', bgcolor: `${labelColor}12`, border: `1px solid ${labelColor}30`, flexShrink: 0 }}>
                    {icon}
                  </Box>
                  <Box>
                    <Box sx={{ display: 'inline-block', px: 1.25, py: 0.4, bgcolor: `${labelColor}12`, border: `1px solid ${labelColor}30`, borderRadius: 1, mb: 0.75 }}>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 10, color: labelColor, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.1rem', md: '1.4rem' }, color: '#f0f0f8', lineHeight: 1.2 }}>{title}</Typography>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', mt: 0.5 }}>{subtitle}</Typography>
                  </Box>
                </Box>

                <Stack spacing={1.25} sx={{ mb: 3 }}>
                  {beats.map((beat, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: labelColor, mt: 0.75, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{beat}</Typography>
                    </Box>
                  ))}
                </Stack>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', pt: 2.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <CheckCircleIcon sx={{ color: outcomeColor, fontSize: 16, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: outcomeColor }}>Outcome: {outcome}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Escalation paths */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Escalation Paths
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            When it's serious, the paper trail is built for you.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 580 }}>
            For True Alerts, reviewers select from built-in escalation paths. Every report includes timestamped evidence, chain-of-custody metadata, and a reference key that holds up in court.
          </Typography>

          <Grid container spacing={2.5}>
            {escalationPaths.map(({ icon, title, body, color }) => (
              <Grid key={title} size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, height: '100%', transition: 'border-color 0.2s', '&:hover': { borderColor: `${color}30` } }}>
                  <Box sx={{ mb: 1.5 }}>{icon}</Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(92,225,230,0.1)', py: { xs: 10, md: 14 }, px: 3, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#f0f0f8', letterSpacing: '-0.03em', mb: 2, lineHeight: 1.1 }}>
            Ready to see it live?
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 18 }, color: '#9499b0', lineHeight: 1.7, mb: 5 }}>
            Book a 30-minute demo and we'll walk through your facility's specific scenario — room count, staff size, and the alert types most relevant to your risk profile.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 17, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 32px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}
          >
            Book a Demo →
          </Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
